/**
 * Owner daily report (MD-0128). The Telegram Mini App signs in with Telegram's launch
 * data and then reads with its own bearer token, without cookies; an admin in a browser
 * reads with the ordinary web-admin session.
 */
import { fetchBlob, get, post } from './client';

export type Money = string;

export interface OwnerWindows {
  day: Money;
  prior_day: Money;
  week: Money;
  prior_week: Money;
}

export interface OwnerGoodsGroup {
  revenue: Money;
  prior_revenue: Money;
  profit: Money;
  prior_profit: Money;
  unpriced_revenue: Money;
  /** Revenue whose profit is the owner's «наценка 50%» estimate (old resale with no cost). */
  estimated_revenue: Money;
}

export interface OwnerGoods {
  own: OwnerGoodsGroup;
  frozen: OwnerGoodsGroup;
  hot: OwnerGoodsGroup;
  resale: OwnerGoodsGroup;
}

export interface OwnerUsual {
  weeks: number;
  amount: Money;
  low: Money;
  high: Money;
  record_weeks: number;
  checks: Money;
  avg_check: Money;
}

export interface OwnerMissingProduct {
  product_name: string;
  last_sale_at: string | null;
  usual_late_revenue: Money;
}

export interface OwnerStoreDay {
  location_id: string;
  name: string;
  till: OwnerWindows;
  checks: number;
  prior_checks: number;
  usual: OwnerUsual | null;
  goods: OwnerGoods;
  missing: OwnerMissingProduct[];
}

export interface OwnerAlert {
  location_id: string;
  kind: 'drop' | 'record';
}

export interface OwnerProductionFlow {
  produced_kg: Money;
  produced_pcs: Money;
  to_stores_kg: Money;
  sold_kg: Money;
}

export interface OwnerDayReport {
  date: string;
  compare_date: string;
  week_from: string;
  prior_week_from: string;
  as_of: string | null;
  till: OwnerWindows;
  checks: number;
  prior_checks: number;
  goods: OwnerGoods;
  payments: { card: Money; cash: Money; other: Money };
  packs: { count: number; prior_count: number; revenue: Money; profit: Money | null };
  production: { day: OwnerProductionFlow; week: OwnerProductionFlow };
  stores: OwnerStoreDay[];
  alerts: OwnerAlert[];
  reader_name: string;
}

export type OwnerPeriod = 'day' | 'week' | 'month' | 'year';

/** Hour ("08") on the day curve, ISO date on a period curve. */
export interface OwnerSeriesPoint {
  key: string;
  current: Money | null;
  compare: Money | null;
}

/** Till of a day; the rest after frozen, hot and resale is checkout bags and packs. */
export interface OwnerDayRevenue {
  date: string;
  revenue: Money;
  frozen: Money;
  hot: Money;
  resale: Money;
}

export interface OwnerProductionDay {
  date: string;
  produced_kg: Money;
  to_stores_kg: Money;
  sold_kg: Money;
}

/**
 * A month of the year view. Profit is gross: net revenue minus the current cost basis,
 * so months compare like with like. `prior_*` is the same month a year earlier, null
 * when ERP has no sales for it.
 */
export interface OwnerMonthRow {
  month: string;
  days: number;
  complete: boolean;
  revenue: Money;
  frozen: Money;
  hot: Money;
  resale: Money;
  frozen_profit: Money;
  hot_profit: Money;
  resale_profit: Money;
  unpriced: Money;
  checks: number;
  prior_revenue: Money | null;
  prior_profit: Money | null;
}

export interface OwnerStorePeriod {
  location_id: string;
  name: string;
  revenue: Money;
  compare: Money;
  usual: Money | null;
  checks: number;
  compare_checks: number;
  goods: OwnerGoods;
}

/** «Что изменилось»: a store or the network (location null) between two windows. */
export interface OwnerChangeCard {
  location_id: string | null;
  name: string;
  revenue: Money;
  prior_revenue: Money;
  profit: Money;
  prior_profit: Money;
  own_share: Money | null;
  prior_own_share: Money | null;
  margin: Money | null;
  prior_margin: Money | null;
}

export interface OwnerChanges {
  date_from: string;
  date_to: string;
  compare_from: string;
  compare_to: string;
  cards: OwnerChangeCard[];
}

export interface OwnerMonthForecast {
  projected: Money;
  days: number;
  month_days: number;
  prior_month: Money | null;
}

export interface OwnerProductProfit {
  name: string;
  group: 'own' | 'resale';
  revenue: Money;
  profit: Money;
  margin: Money | null;
  prior_profit: Money;
}

export interface OwnerProducts {
  period: OwnerPeriod;
  top: OwnerProductProfit[];
  rising: OwnerProductProfit[];
  falling: OwnerProductProfit[];
  unpriced: { name: string; revenue: Money }[];
  unpriced_total: Money;
}

/** Write-offs by reason: spoiled is the loss; given and other are shown beside it. */
export type OwnerWriteOffKind = 'spoiled' | 'given' | 'other';

export interface OwnerLossMonth {
  month: string;
  complete: boolean;
  profit: Money;
  spoiled: Money;
  given: Money;
  other: Money;
  unpriced_lines: number;
  /** TGTG/Foodsi pack result; null before the platform data. */
  packs: Money | null;
  packs_estimated: boolean;
  losses: Money;
  loss_share: Money | null;
  sales_days: number;
  shift_days: number;
  /** Set only when every day with sales has a seller shift. */
  hours: Money | null;
  hours_profit: Money | null;
  profit_per_hour: Money | null;
}

export interface OwnerLossStore {
  location_id: string;
  name: string;
  profit: Money;
  spoiled: Money;
  given: Money;
  other: Money;
  packs: Money | null;
  losses: Money;
  loss_share: Money | null;
  hours: Money | null;
  hours_profit: Money | null;
  profit_per_hour: Money | null;
  hours_months: string[];
}

export interface OwnerWrittenOffProduct {
  name: string;
  quantity: string;
  unit_code: string | null;
  kind: OwnerWriteOffKind;
  cost: Money | null;
}

export interface OwnerLosses {
  period: 'month' | 'year';
  date_from: string;
  date: string;
  location_id: string | null;
  months: OwnerLossMonth[];
  stores: OwnerLossStore[];
  products: OwnerWrittenOffProduct[];
  write_offs_from: string | null;
  packs_from: string;
}

export interface OwnerDashboard {
  period: OwnerPeriod;
  date: string;
  date_from: string;
  compare_from: string;
  compare_to: string;
  as_of: string | null;
  revenue: Money;
  compare: Money;
  checks: number;
  compare_checks: number;
  goods: OwnerGoods;
  stores: OwnerStorePeriod[];
  curve: OwnerSeriesPoint[];
  trend: OwnerDayRevenue[];
  production: OwnerProductionDay[];
  months: OwnerMonthRow[];
  history_from: string | null;
  compare_complete: boolean;
  changes: OwnerChanges | null;
  forecast: OwnerMonthForecast | null;
  day: OwnerDayReport | null;
  reader_name: string;
}

/** A product's stock now (values of a positive balance; null when not known) and sales. */
export interface OwnerStockProduct {
  name: string;
  quantity: Money;
  unit_code: string | null;
  cost_value: Money | null;
  retail_value: Money | null;
  /** zł in the period. */
  sold: Money;
  /** In the stock unit; null when it also sold in another unit. */
  sold_quantity: Money | null;
  /** zł in the seven days to the report day. */
  week_sold: Money;
  week_quantity: Money | null;
  /** Days the stock lasts at the seven-day pace; null when it did not sell. */
  cover_days: Money | null;
  running_low: boolean;
}

/** A category of a store's own or resale goods: totals and products, largest first. */
export interface OwnerStockCategory {
  group: 'own' | 'resale';
  name: string;
  cost_value: Money;
  retail_value: Money;
  stock_kg: Money | null;
  sold: Money;
  week_sold: Money;
  cover_days: Money | null;
  running_low: number;
  products: OwnerStockProduct[];
}

export interface OwnerStoreDetail {
  location_id: string;
  name: string;
  period: OwnerPeriod;
  curve: OwnerSeriesPoint[];
  trend: OwnerDayRevenue[];
  months: OwnerMonthRow[];
  stock: OwnerStockCategory[];
  missing: OwnerMissingProduct[];
}

export interface OwnerViewerSession {
  token: string;
  expires_at: string;
  viewer_name: string;
}

export function signInOwnerViewer(initData: string): Promise<OwnerViewerSession> {
  return post<OwnerViewerSession>(
    '/api/web-admin/owner-report/auth/telegram',
    { init_data: initData },
    { credentials: 'omit', skipAuthRedirect: true },
  );
}

/** A Telegram viewer reads with its token and without cookies; an admin with the session. */
function readerOptions(token: string | null) {
  return token
    ? { credentials: 'omit' as const, headers: { Authorization: `Bearer ${token}` } }
    : { skipAuthRedirect: true };
}

/** The day the report opens on without a date: before noon the last day with sales. */
export function getOwnerDefaultDay(token: string | null): Promise<{ date: string }> {
  return get<{ date: string }>('/api/web-admin/analytics/owner-report/default-day', {
    ...readerOptions(token),
  });
}

export function getOwnerDashboard(
  date: string,
  period: OwnerPeriod,
  token: string | null,
): Promise<OwnerDashboard> {
  return get<OwnerDashboard>('/api/web-admin/analytics/owner-report/dashboard', {
    params: { date, period },
    ...readerOptions(token),
  });
}

export function getOwnerStoreDetail(
  date: string,
  period: OwnerPeriod,
  locationId: string,
  token: string | null,
): Promise<OwnerStoreDetail> {
  return get<OwnerStoreDetail>('/api/web-admin/analytics/owner-report/dashboard/store', {
    params: { date, period, location_id: locationId },
    ...readerOptions(token),
  });
}

export interface OwnerCameraEvidence {
  sha256: string;
  camera_id: string;
  captured_at: string;
}
export type ApronExtent = 'all_day' | 'half_day' | 'morning' | 'midday' | 'evening' | 'moment';
export interface OwnerCameraFinding {
  id: string;
  kind: string;
  text: string;
  start: string;
  end: string;
  evidence: OwnerCameraEvidence[];
  /** Apron only: how much of the day; hourly checks since the night of 23.10.2026. */
  extent?: ApronExtent | null;
  hours_off?: number | null;
  hours_checked?: number | null;
}
export interface OwnerCameraStore {
  store_id: string;
  name: string;
  hours: string;
  availability: 'reviewed' | 'limited' | 'unavailable' | 'closed';
  first_seen: string | null;
  last_seen: string | null;
  last_seen_status: 'verified' | 'still_present' | 'unavailable';
  absence_minutes: number | null;
  total_complete: boolean;
  /** A gap at the till; its frames: before, the empty till, back (owner 22.10.2026). */
  intervals: { start: string; end: string; minutes: number; evidence?: OwnerCameraEvidence[] }[];
  findings: OwnerCameraFinding[];
  dismissed_count: number;
  unresolved_count: number;
}
export interface OwnerCameraDay {
  date: string;
  revision: string;
  created_at: string;
  stores: OwnerCameraStore[];
  problems: { group: 'offline' | 'obstructed' | 'blind_spots'; stores: string[] }[];
}
export function getOwnerCameras(date: string, token: string | null) {
  return get<{ date: string; report: OwnerCameraDay | null }>(
    '/api/web-admin/analytics/owner-report/cameras',
    {
      params: { date },
      ...readerOptions(token),
    },
  );
}
export async function getOwnerCameraImage(
  date: string,
  sha: string,
  token: string | null,
): Promise<Blob> {
  const { blob } = await fetchBlob(
    `/api/web-admin/analytics/owner-report/cameras/evidence/${encodeURIComponent(sha)}`,
    {
      params: { date },
      ...readerOptions(token),
    },
  );
  return blob;
}

export function getOwnerLosses(
  date: string,
  period: 'month' | 'year',
  locationId: string | null,
  token: string | null,
): Promise<OwnerLosses> {
  return get<OwnerLosses>('/api/web-admin/analytics/owner-report/dashboard/losses', {
    params: { date, period, ...(locationId ? { location_id: locationId } : {}) },
    ...readerOptions(token),
  });
}

export function getOwnerProducts(
  date: string,
  period: OwnerPeriod,
  locationId: string | null,
  token: string | null,
): Promise<OwnerProducts> {
  return get<OwnerProducts>('/api/web-admin/analytics/owner-report/dashboard/products', {
    params: { date, period, ...(locationId ? { location_id: locationId } : {}) },
    ...readerOptions(token),
  });
}
